import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1722963295936-03ea25f1d4d2?auto=format&fit=crop&w=900&q=72";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      nav(u.role === "seller" || u.role === "admin" ? "/seller" : (loc.state?.from || "/account"));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/40" />
        <Link to="/" className="absolute top-8 left-8 text-sand font-serif text-2xl">handmade<span className="italic">giftkart</span></Link>
        <div className="absolute bottom-10 left-10 right-10 text-sand">
          <p className="font-serif text-4xl leading-tight tracking-tight">Welcome back to the<br /> home of handmade.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-sand">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-mute hover:text-clay mb-8"><ArrowLeft className="w-4 h-4" /> Back home</Link>
          <div className="overline text-clay mb-3">Sign in</div>
          <h1 className="font-serif text-4xl tracking-tight mb-8">Good to see you.</h1>
          {err && <div className="bg-destructive/10 text-destructive text-sm rounded-sm px-4 py-3 mb-4" data-testid="login-error">{err}</div>}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-mute block mb-1.5">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="login-email" />
            </div>
            <div>
              <label className="overline text-mute block mb-1.5">Password</label>
              <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-full bg-clay text-sand py-3.5 text-sm hover:bg-forest transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>
          <p className="text-sm text-mute mt-6">New here? <Link to="/register" className="text-clay link-underline">Create an account</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
